import { z } from 'zod';
/** Storage (docs 73–76): one S3-compatible bucket per user; Couch only uses keys below this prefix. */
export const STORAGE_PREFIX = 'couch/';
export const STORAGE_CHECK_OBJECT = `${STORAGE_PREFIX}.couch-check`;
export const StoragePresetIdSchema = z.enum(['r2', 'ovh', 'hetzner', 'scaleway', 'backblaze', 'gcs', 'other']);
const REGION = /^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/;
export const STORAGE_PRESETS = {
    r2: {
        id: 'r2', label: 'Cloudflare R2',
        place: { label: 'Account ID', pattern: /^[a-f0-9]{32}(\.eu)?$/, example: '32 hex characters from the R2 overview; add .eu for a bucket in the EU jurisdiction' },
        endpoint: (account) => `https://${account}.r2.cloudflarestorage.com`, region: () => 'auto',
        console: 'https://dash.cloudflare.com/?to=/:account/r2', egress: 'Downloads are free.',
    },
    ovh: {
        id: 'ovh', label: 'OVHcloud',
        place: { label: 'Region', pattern: REGION, example: 'gra, sbg, de, waw …' },
        endpoint: (region) => `https://s3.${region}.io.cloud.ovh.net`, region: (region) => region,
        console: 'https://www.ovh.com/manager/#/public-cloud', egress: 'See OVHcloud’s price list for downloads.',
    },
    hetzner: {
        id: 'hetzner', label: 'Hetzner',
        place: { label: 'Location', pattern: /^(fsn1|nbg1|hel1)$/, example: 'fsn1, nbg1 or hel1' },
        endpoint: (location) => `https://${location}.your-objectstorage.com`, region: (location) => location,
        console: 'https://console.hetzner.com/', egress: '1 TB of downloads a month is included, then per TB.',
    },
    scaleway: {
        id: 'scaleway', label: 'Scaleway',
        place: { label: 'Region', pattern: /^(fr-par|nl-ams|pl-waw)$/, example: 'fr-par, nl-ams or pl-waw' },
        endpoint: (region) => `https://s3.${region}.scw.cloud`, region: (region) => region,
        console: 'https://console.scaleway.com/object-storage/buckets', egress: '75 GB of downloads a month are free, then per GB.',
    },
    backblaze: {
        id: 'backblaze', label: 'Backblaze B2',
        place: { label: 'Region', pattern: /^[a-z]{2}-[a-z]+-\d{3}$/, example: 'eu-central-003 (from the bucket’s endpoint)' },
        endpoint: (region) => `https://s3.${region}.backblazeb2.com`, region: (region) => region,
        console: 'https://secure.backblaze.com/b2_buckets.htm', egress: 'Downloads up to three times what you store are free.',
    },
    gcs: {
        id: 'gcs', label: 'Google Cloud Storage (HMAC key)', place: null,
        endpoint: () => 'https://storage.googleapis.com', region: () => 'auto',
        console: 'https://console.cloud.google.com/storage/settings;tab=interoperability',
        egress: 'Downloads to the internet cost per GB; to a Google VM in the same region they are free.',
    },
    other: {
        id: 'other', label: 'Other S3-compatible',
        place: { label: 'Endpoint', pattern: /^https:\/\/[a-z0-9.-]+(:\d{2,5})?$/, example: 'https://s3.example.com' },
        endpoint: (endpoint) => endpoint, region: () => 'us-east-1',
        console: '', egress: 'Check what your provider charges for downloads.',
    },
};
/** The stored key (compact JSON). `provider` is the preset; the endpoint is an https origin, path-style requests. */
export const StorageKeySchema = z.object({
    provider: StoragePresetIdSchema,
    endpoint: z.string().regex(/^https:\/\/[a-z0-9.-]+(:\d{2,5})?$/, 'The endpoint is an https address without a path'),
    region: z.string().regex(/^[a-z0-9-]{2,32}$/, 'That does not look like a region'),
    // S3's bucket rules, minus dots (path-style works for all of them, and dots trip up TLS on some providers).
    bucket: z.string().regex(/^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/, 'Bucket names are 3–63 lower-case letters, digits and hyphens'),
    accessKeyId: z.string().regex(/^[A-Za-z0-9_\-+/=.]{8,128}$/, 'That does not look like an access key id'),
    secretAccessKey: z.string().min(16, 'That does not look like a secret access key').max(256).regex(/^\S+$/, 'The secret has no spaces'),
});
/** What the browser form (or the agent's prompts) collects. `region` overrides the preset's for "other". */
export const StorageFormSchema = z.object({
    preset: StoragePresetIdSchema,
    place: z.string().trim().max(200).default(''),
    region: z.string().trim().max(32).optional(),
    bucket: z.string().trim(),
    accessKeyId: z.string().trim(),
    secretAccessKey: z.string().trim(),
});
/** The key a form describes, or what is wrong with it. Nothing of the secret is echoed. */
export function storageKeyFromForm(input) {
    const form = StorageFormSchema.safeParse(input);
    if (!form.success)
        return { problem: 'Fill in every field' };
    const f = form.data;
    const preset = STORAGE_PRESETS[f.preset];
    const place = preset.place ? f.place.toLowerCase().replace(/\/+$/, '') : '';
    if (preset.place && !preset.place.pattern.test(place))
        return { problem: `${preset.place.label}: ${preset.place.example}` };
    const region = f.preset === 'other' && f.region ? f.region : preset.region(place);
    const key = StorageKeySchema.safeParse({
        provider: f.preset, endpoint: preset.endpoint(place), region, bucket: f.bucket, accessKeyId: f.accessKeyId, secretAccessKey: f.secretAccessKey,
    });
    return key.success ? key.data : { problem: key.error.issues[0]?.message ?? 'That is not a usable storage key' };
}
/** Reads a stored key back; undefined when it is not one. */
export function parseStorageKey(text) {
    try {
        const parsed = StorageKeySchema.safeParse(JSON.parse(text));
        return parsed.success ? parsed.data : undefined;
    }
    catch {
        return undefined;
    }
}
export const compactStorageKey = (key) => JSON.stringify(StorageKeySchema.parse(key));
export const SetStorageKeyRequestSchema = StorageFormSchema.extend({
    acknowledged: z.literal(true, { message: 'Confirm that you understand what the key can do' }),
});
//# sourceMappingURL=storage.js.map